import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Badge,
  Flex,
  VStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  Select,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../lib/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc, 
  query, 
  where,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { Prediction, Match, User } from '../../types';

interface PredictionWithDetails extends Prediction {
  match?: Match;
  user?: {
    id: string;
    displayName: string | null;
    email: string | null;
  };
}

const ManagePredictions: React.FC = () => {
  const [predictions, setPredictions] = useState<PredictionWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState('');
  const [phases, setPhases] = useState<string[]>([]);
  const toast = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadPhases();
  }, []);

  useEffect(() => {
    if (selectedPhase) {
      loadPredictions();
    }
  }, [selectedPhase]);

  const loadPhases = async () => {
    try {
      const matchesRef = collection(db, 'matches');
      const matchesSnap = await getDocs(matchesRef);
      const uniquePhases = Array.from(new Set(matchesSnap.docs.map(doc => doc.data().phase)));
      setPhases(uniquePhases);
    } catch (error) {
      console.error('Error al cargar fases:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las fases',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const loadPredictions = async () => {
    setLoading(true);
    try {
      // 1. Obtener partidos de la fase seleccionada
      const matchesRef = collection(db, 'matches');
      const matchesQuery = query(matchesRef, where('phase', '==', selectedPhase));
      const matchesSnap = await getDocs(matchesQuery);
      const matchesData = matchesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Match[];
      const matchIds = matchesData.map(match => match.id);

      // 2. Obtener predicciones para esos partidos
      const predictionsRef = collection(db, 'predictions');
      const predictionsQuery = query(
        predictionsRef,
        where('match_id', 'in', matchIds)
      );
      const predictionsSnap = await getDocs(predictionsQuery);
      const predictionsData = predictionsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Prediction[];

      // 3. Obtener usuarios
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      const usersData = usersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as User[];

      // 4. Combinar datos
      const predictionsWithDetails = predictionsData.map(prediction => {
        const match = matchesData.find(m => m.id === prediction.match_id);
        const user = usersData.find(u => u.uid === prediction.user_id);
        return {
          ...prediction,
          match,
          user: user ? {
            id: user.id,
            displayName: user.displayName,
            email: user.email
          } : undefined
        };
      });

      setPredictions(predictionsWithDetails);
    } catch (error) {
      console.error('Error al cargar predicciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las predicciones',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrediction = async (predictionId: string, scoreA: number, scoreB: number) => {
    try {
      const predictionRef = doc(db, 'predictions', predictionId);
      await updateDoc(predictionRef, {
        predicted_score_a: scoreA,
        predicted_score_b: scoreB,
        updated_at: Timestamp.now()
      });

      toast({
        title: 'Éxito',
        description: 'Predicción actualizada correctamente',
        status: 'success',
        duration: 3000,
      });

      loadPredictions();
    } catch (error) {
      console.error('Error al actualizar predicción:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la predicción',
        status: 'error',
        duration: 3000,
      });
    }
  };

  if (!user || user.email !== 'htorres@teiker.mx') {
    return (
      <Box p={4}>
        <Heading size="md">No tienes acceso a esta página</Heading>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch" p={4}>
      <Heading>Administración de Pronósticos</Heading>
      
      <Card>
        <CardHeader>
          <Heading size="md">Seleccionar Fase</Heading>
        </CardHeader>
        <CardBody>
          <FormControl>
            <FormLabel>Fase</FormLabel>
            <Select 
              value={selectedPhase} 
              onChange={(e) => setSelectedPhase(e.target.value)}
              placeholder="Selecciona la fase"
            >
              {phases.map((phase) => (
                <option key={phase} value={phase}>{phase}</option>
              ))}
            </Select>
          </FormControl>
        </CardBody>
      </Card>

      {selectedPhase && (
        <Card>
          <CardHeader>
            <Heading size="md">Pronósticos de {selectedPhase}</Heading>
          </CardHeader>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Usuario</Th>
                  <Th>Partido</Th>
                  <Th>Pronóstico Local</Th>
                  <Th>Pronóstico Visitante</Th>
                  <Th>Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {predictions.map((prediction) => (
                  <Tr key={prediction.id}>
                    <Td>{prediction.user?.displayName || prediction.user?.email || 'Usuario desconocido'}</Td>
                    <Td>
                      {prediction.match ? (
                        `${prediction.match.team_a} vs ${prediction.match.team_b}`
                      ) : (
                        'Partido no encontrado'
                      )}
                    </Td>
                    <Td>
                      <NumberInput
                        min={0}
                        max={20}
                        value={prediction.predicted_score_a}
                        onChange={(_, value) => {
                          const newPredictions = predictions.map(p => 
                            p.id === prediction.id 
                              ? { ...p, predicted_score_a: value }
                              : p
                          );
                          setPredictions(newPredictions);
                        }}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </Td>
                    <Td>
                      <NumberInput
                        min={0}
                        max={20}
                        value={prediction.predicted_score_b}
                        onChange={(_, value) => {
                          const newPredictions = predictions.map(p => 
                            p.id === prediction.id 
                              ? { ...p, predicted_score_b: value }
                              : p
                          );
                          setPredictions(newPredictions);
                        }}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </Td>
                    <Td>
                      <Button
                        colorScheme="blue"
                        size="sm"
                        onClick={() => handleUpdatePrediction(
                          prediction.id,
                          prediction.predicted_score_a,
                          prediction.predicted_score_b
                        )}
                      >
                        Actualizar
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default ManagePredictions; 